/**
 * Medals, derived from the solved ideal lap.
 *
 * A bare lap time is a measurement, not a goal — "+18.45s off ideal" tells you
 * where you are but gives you nothing to aim at. A graduated ladder turns the
 * same number into a target two seconds away, and because every threshold is a
 * multiple of the solver's own lap, the difficulty scales itself to whatever
 * circuit the day produced. No hand-tuning per track.
 */

export type MedalId = 'solver' | 'gold' | 'silver' | 'bronze' | 'none';

export interface Medal {
  id: MedalId;
  name: string;
  /** Multiple of the ideal lap you must beat. */
  factor: number;
  color: string;
  emoji: string;
}

/** Ordered fastest-first, which is the order `medalFor` relies on. */
export const MEDALS: Medal[] = [
  // Solver was 1.04 when the lap launched already at speed; with a standing
  // start every run now pays the launch deficit (accelerating from 0 to race
  // speed), so it is loosened to stay reachable — but kept clearly tighter
  // than Gold (1.1) so it remains the top medal.
  { id: 'solver', name: 'Solver', factor: 1.07, color: '#b06bff', emoji: '🟣' },
  { id: 'gold', name: 'Gold', factor: 1.1, color: '#e8c14a', emoji: '🥇' },
  { id: 'silver', name: 'Silver', factor: 1.18, color: '#c8ccd4', emoji: '🥈' },
  { id: 'bronze', name: 'Bronze', factor: 1.3, color: '#c98b52', emoji: '🥉' },
];

export const NO_MEDAL: Medal = {
  id: 'none',
  name: 'No medal',
  factor: Infinity,
  color: '#5c6675',
  emoji: '⬛',
};

/** Target time for a medal on a circuit with this ideal lap, in ms. */
export function medalTime(idealMs: number, medal: Medal): number {
  return idealMs * medal.factor;
}

/** The best medal a lap time earns. */
export function medalFor(timeMs: number, idealMs: number): Medal {
  for (const medal of MEDALS) {
    if (timeMs <= medalTime(idealMs, medal)) return medal;
  }
  return NO_MEDAL;
}

/** The next one up, and how much time you need to find. Null once at Solver. */
export function nextMedal(
  timeMs: number,
  idealMs: number,
): { medal: Medal; gapMs: number } | null {
  // Walk slowest-first and take the first target still ahead of the player.
  for (let i = MEDALS.length - 1; i >= 0; i--) {
    const target = medalTime(idealMs, MEDALS[i]);
    if (timeMs > target) return { medal: MEDALS[i], gapMs: timeMs - target };
  }
  return null;
}

export function medalById(id: string | null | undefined): Medal {
  return MEDALS.find((m) => m.id === id) ?? NO_MEDAL;
}

/** True when `a` is a better medal than `b`. */
export function isBetterMedal(a: MedalId, b: MedalId | null | undefined): boolean {
  const rank = (id: MedalId | null | undefined): number => {
    const i = MEDALS.findIndex((m) => m.id === id);
    return i === -1 ? MEDALS.length : i;
  };
  return rank(a) < rank(b);
}
